import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/core/services/api.service';
import { PaginationService } from 'src/app/core/services/pagination.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OtpVerificationModalComponent } from 'src/app/shared/components/otp-verification-modal/otp-verification-modal.component';
import Swal from 'sweetalert2';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-material-acceptance-list',
  templateUrl: './material-acceptance-list.component.html',
  styleUrls: ['./material-acceptance-list.component.scss'],
  standalone: false
})
export class MaterialAcceptanceListComponent implements OnInit, OnDestroy {
  loading = false;
  error: string | null = null;
  data: any[] = [];
  filtered: any[] = [];
  visible: any[] = [];
  breadCrumbItems!: Array<{}>;
  searchTerm: string = '';
  statusFilter: string = '';
  isDownloading = false;
  
  // Inline form state
  showForm = false;
  form!: FormGroup;
  submitted = false;
  isEditMode = false;
  editId: any = null;
  poList: any[] = [];
  poItems: any[] = [];
  poSearchTerm: string = '';
  poLoading: boolean = false;
  private searchSubject = new Subject<string>();

  get items(): FormArray { return this.form.get('items') as FormArray; }

  constructor(private api: ApiService, private router: Router, private fb: FormBuilder, public paginationService: PaginationService, private modalService: NgbModal) {}

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: 'Stock Management' },
      { label: 'Material Acceptances', active: true }
    ];
    this.fetch();
    this.initForm();
    this.loadPurchaseOrders();
    
    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.loadPurchaseOrders(searchTerm);
    });
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  toggleForm(): void {
    this.showForm = !this.showForm;
    if (this.showForm) {
      this.isEditMode = false;
      this.editId = null;
      this.submitted = false;
      this.resetForm();
    }
  }

  resetForm(): void {
    const today = this.formatDateForInput(new Date());
    this.form.reset({
      purchase_order_id: null,
      received_date: today,
      invoice_number: '',
      invoice_date: today,
      items: []
    });
    while (this.items?.length > 0) { this.items.removeAt(0); }
    this.poItems = [];
  }

  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private initForm(): void {
    this.form = this.fb.group({
      purchase_order_id: [null, Validators.required],
      received_date: ['', Validators.required],
      invoice_number: [''],
      invoice_date: [''],
      items: this.fb.array([])
    });

    this.form.get('purchase_order_id')?.valueChanges.subscribe((poId) => {
      if (poId) {
        const order = (this.poList || []).find((po: any) => po.id === poId);
        const orderItems = Array.isArray(order?.items) ? order.items : [];
        this.poItems = orderItems.map((it: any) => ({
          purchase_order_item_id: it.id,
          item_id: it.itemId ?? it.item?.id,
          ordered_quantity: Number(it.pending_quantity) || 0,
          received_quantity: 0,
          itemName: it.item?.name || `Item #${it.itemId}`,
          unitSymbol: it.item?.unit?.symbol || it.item?.unit_symbol || '-'
        }));
        this.items.clear();
        this.poItems.forEach((row: any) => this.items.push(this.createItemRow(row)));
      } else {
        this.poItems = [];
        this.items.clear();
      }
    });
  }

  private createItemRow(row: any): FormGroup {
    return this.fb.group({
      purchase_order_item_id: [row.purchase_order_item_id, Validators.required],
      item_id: [row.item_id, Validators.required],
      ordered_quantity: [{ value: row.ordered_quantity, disabled: true }],
      received_quantity: [row.received_quantity, [Validators.required, Validators.min(0)]]
    });
  }

  onItemChange(i: number): void {
    const g = this.items.at(i) as FormGroup;
    const received = Number(g.get('received_quantity')?.value || 0);
    const ordered = Number(g.get('ordered_quantity')?.value || 0);

    // Ensure received quantity doesn't exceed ordered quantity
    if (received > ordered) {
      g.patchValue({ received_quantity: ordered }, { emitEvent: false });
    }
  }

  submit(): void {
    this.submitted = true;
    if (this.form.invalid || this.items.length === 0) {
      Swal.fire('Error', 'Please fill all required fields', 'error');
      return;
    }
    
    // Open OTP verification modal
    const modalRef = this.modalService.open(OtpVerificationModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
    
    modalRef.componentInstance.transactionType = 'material_acceptance';
    modalRef.componentInstance.operation = this.isEditMode ? 'update' : 'create';
    
    modalRef.result.then(
      (result) => {
        if (result?.verified) {
          // OTP verified, proceed with save
          this.proceedWithSubmit(result.verificationHash);
        }
      },
      () => {
        // Modal dismissed/cancelled
      }
    );
  }

  /**
   * Proceed with submit after OTP verification
   */
  private proceedWithSubmit(verificationHash: string): void {
    const payload = {
      ...this.form.getRawValue(),
      verification_hash: verificationHash,
      items: (this.form.getRawValue().items || []).map((it: any, idx: number) => ({
        ...it,
        ordered_quantity: this.items.at(idx).get('ordered_quantity')?.value || 0,
      }))
    };
    this.loading = true;
    
    if (this.isEditMode && this.editId) {
      // Update existing material acceptance
      this.api.put(`/material_acceptances/${this.editId}`, payload).subscribe({
        next: (response: any) => {
          this.loading = false;
          if (response.success) {
            Swal.fire('Success', 'Material acceptance updated successfully', 'success');
            this.toggleForm();
            this.fetch();
            this.loadPurchaseOrders();
          } else {
            Swal.fire('Error', response.message || 'Failed to update material acceptance', 'error');
          }
        },
        error: (err) => {
          this.loading = false;
          Swal.fire('Error', err?.message || 'Failed to update material acceptance', 'error');
        }
      });
    } else {
      // Create new material acceptance
      this.api.createMaterialAcceptance(payload).subscribe({
        next: (response: any) => {
          this.loading = false;
          if (response.success) {
            Swal.fire('Success', 'Material acceptance created successfully', 'success');
            this.toggleForm();
            this.fetch();
            this.loadPurchaseOrders();
          } else {
            Swal.fire('Error', response.message || 'Failed to create material acceptance', 'error');
          }
        },
        error: (err) => {
          this.loading = false;
          Swal.fire('Error', err?.message || 'Failed to create material acceptance', 'error');
        }
      });
    }
  }

  fetch(): void {
    this.loading = true;
    this.error = null;
    this.api.get<any>('/material_acceptances')
      .subscribe({
        next: (res: any) => {
          this.data = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
          this.applyFilters();
          this.changePage();
          this.loading = false;
        },
        error: (err: any) => {
          this.error = err?.message || 'Failed to load material acceptances';
          this.loading = false;
        }
      });
  }

  loadPurchaseOrders(searchTerm: string = ''): void {
    const endpoint = searchTerm 
      ? `/purchase_order_list?po_number=${encodeURIComponent(searchTerm)}`
      : '/purchase_order_list';
    
    this.poLoading = true;
    this.api.get<any>(endpoint).subscribe({
      next: (res: any) => {
        this.poList = res?.data || [];
        this.poLoading = false;
      },
      error: (err) => {
        console.error('Error loading purchase orders:', err);
        this.poList = [];
        this.poLoading = false;
      }
    });
  }

  onPoSearch(event: any): void {
    const searchTerm = event?.term || '';
    this.searchSubject.next(searchTerm);
  }

  performSearch(): void {
    this.applyFilters();
    this.paginationService.page = 1;
    this.changePage();
  }

  filterByStatus(): void {
    this.applyFilters();
    this.paginationService.page = 1;
    this.changePage();
  }

  private applyFilters(): void {
    const term = (this.searchTerm || '').trim().toLowerCase();
    const status = (this.statusFilter || '').toLowerCase();
    this.filtered = (this.data || []).filter((row: any) => {
      const matchesTerm = !term ||
        String(row?.grnNumber || '').toLowerCase().includes(term) ||
        String(row?.purchaseOrder?.poNumber || '').toLowerCase().includes(term) ||
        String(row?.status || '').toLowerCase().includes(term) ||
        String(row?.receivedBy || '').toLowerCase().includes(term);
      const matchesStatus = !status || String(row?.status || '').toLowerCase() === status;
      return matchesTerm && matchesStatus;
    });
  }

  changePage(): void {
    const source: any[] = Array.isArray(this.filtered) ? this.filtered : [];
    this.visible = this.paginationService.changePage(source);
  }

  getStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'accepted' || s === 'received' || s === 'approved') return 'bg-success-subtle text-success';
    if (s === 'partial' || s === 'pending' || s === 'draft') return 'bg-warning-subtle text-warning';
    if (s === 'rejected' || s === 'cancelled') return 'bg-danger-subtle text-danger';
    return 'bg-secondary-subtle text-secondary';
  }

  /**
   * Edit Material Acceptance
   */
  editMaterialAcceptance(index: number): void {
    this.showForm = true;
    this.isEditMode = true;
    const record = this.visible[index];
    this.editId = record.id || record._id;
    
    // Clear items array
    while (this.items.length > 0) {
      this.items.removeAt(0);
    }
    
    // Populate form (without triggering purchase_order_id change)
    this.form.patchValue({
      received_date: record.receivedDate,
      invoice_number: record.invoiceNumber,
      invoice_date: record.invoiceDate
    }, { emitEvent: false });
    
    // Set purchase order ID without triggering change event
    this.form.get('purchase_order_id')?.setValue(record.purchaseOrderId, { emitEvent: false });
    
    // Manually set poItems for display
    const order = (this.poList || []).find((po: any) => po.id === record.purchaseOrderId);
    if (order && order.items) {
      this.poItems = order.items.map((it: any) => ({
        purchase_order_item_id: it.id,
        item_id: it.itemId ?? it.item?.id,
        ordered_quantity: Number(it.quantity) || 0,
        itemName: it.item?.name || `Item #${it.itemId}`,
        unitSymbol: it.item?.unit?.symbol || it.item?.unit_symbol || '-'
      }));
    }
    
    // Add items from the material acceptance record
    // if (record.items && record.items.length > 0) {
    //   record.items.forEach((item: any) => {
    //     const itemGroup = this.createItemRow({
    //       purchase_order_item_id: item.purchaseOrderItemId,
    //       item_id: item.itemId,
    //       ordered_quantity: item.orderedQuantity,
    //       received_quantity: item.receivedQuantity,
    //       itemName: item.item?.name || `Item #${item.itemId}`
    //     });
    //     this.items.push(itemGroup);
    //   });
    // }

    // Scroll to form
    setTimeout(() => {
      document.getElementById('materialAcceptanceForm')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  /**
   * Delete Material Acceptance
   */
  deleteMaterialAcceptance(id: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        // Open OTP verification modal
        const modalRef = this.modalService.open(OtpVerificationModalComponent, {
          centered: true,
          backdrop: 'static',
          keyboard: false
        });
        
        modalRef.componentInstance.transactionType = 'material_acceptance';
        modalRef.componentInstance.operation = 'delete';
        
        modalRef.result.then(
          (otpResult) => {
            if (otpResult?.verified) {
              // OTP verified, proceed with delete
              this.proceedWithDelete(id, otpResult.verificationHash);
            }
          },
          () => {
            // Modal dismissed/cancelled
          }
        );
      }
    });
  }

  /**
   * Proceed with delete after OTP verification
   */
  private proceedWithDelete(id: any, verificationHash: string): void {
    const payload = { verification_hash: verificationHash };
    this.api.post(`/material_acceptances/${id}/delete`, payload).subscribe({
      next: (response: any) => {
        if (response.success) {
          Swal.fire('Deleted!', 'Material acceptance has been deleted.', 'success');
          this.fetch();
          this.loadPurchaseOrders();
        } else {
          Swal.fire('Error', response.message || 'Failed to delete material acceptance', 'error');
        }
      },
      error: (error) => {
        Swal.fire('Error', error?.message || 'Failed to delete material acceptance', 'error');
      }
    });
  }

  /**
   * Download Material Acceptance PDF
   */
  downloadMaterialAcceptance(id: any): void {
    if (!id) {
      Swal.fire('Error', 'Invalid material acceptance ID', 'error');
      return;
    }

    // Set loading state
    this.isDownloading = true;

    // First, get the download URL from the API
    this.api.get(`/material_acceptances/${id}/download_pdf`).subscribe({
      next: (response: any) => {
        if (response.success && response.data?.url) {
          // Extract the endpoint from the full URL
          const fileUrl = response.data.url;
          
          // Download the file using the URL
          this.api.downloadFile(fileUrl).subscribe({
            next: (blob: Blob) => {
              // Create a blob URL and trigger download
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `material_acceptance_${id}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              
              this.isDownloading = false;
              Swal.fire('Success', 'Material acceptance PDF downloaded successfully', 'success');
            },
            error: (error) => {
              console.error('Error downloading file:', error);
              this.isDownloading = false;
              Swal.fire('Error', 'Failed to download material acceptance PDF', 'error');
            }
          });
        } else {
          this.isDownloading = false;
          Swal.fire('Error', 'Download URL not found in response', 'error');
        }
      },
      error: (error) => {
        console.error('Error downloading material acceptance:', error);
        this.isDownloading = false;
        Swal.fire('Error', 'Failed to download material acceptance PDF', 'error');
      }
    });
  }

}
